
import React from 'react';
import { HelpCircle, BookOpen, ExternalLink } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ContextualHelpProps {
  topic: string;
  description?: string;
  children?: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  showDocLink?: boolean;
}

export function ContextualHelp({ 
  topic, 
  description, 
  children, 
  placement = 'top',
  className,
  showDocLink = true
}: ContextualHelpProps) {
  const helpContent = getHelpContent(topic);
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <HelpCircle 
            className={cn("h-4 w-4 text-muted-foreground/70 hover:text-muted-foreground cursor-help", className)} 
          />
        </TooltipTrigger>
        <TooltipContent side={placement} className="w-80 p-0">
          <div className="flex flex-col">
            <div className="bg-muted-foreground/10 p-2">
              <h5 className="font-medium text-sm">{helpContent.title || topic}</h5>
            </div>
            <div className="p-2 text-xs">
              {description || helpContent.description}
            </div>
            {showDocLink && helpContent.docLink && (
              <div className="bg-muted-foreground/5 border-t p-2">
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-xs flex gap-1 items-center"
                  onClick={() => window.open(helpContent.docLink, '_blank')}
                >
                  <BookOpen className="h-3 w-3" />
                  Read documentation
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ExtendedHelpProps {
  topic: string;
  children?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'inline' | 'button' | 'icon';
  buttonText?: string;
}

export function ExtendedHelp({
  topic,
  children,
  className,
  size = 'md',
  variant = 'button',
  buttonText = 'Help & Guidelines'
}: ExtendedHelpProps) {
  const helpContent = getHelpContent(topic);
  
  const triggerContent = () => {
    switch (variant) {
      case 'inline':
        return <span className={cn("text-blue-600 cursor-pointer underline", className)}>{buttonText}</span>;
      case 'icon':
        return (
          <Button 
            variant="ghost" 
            size="icon"
            className={cn("", className)}
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        );
      default:
        return (
          <Button 
            variant="ghost" 
            size="sm"
            className={cn("flex items-center gap-1", className)}
          >
            <HelpCircle className="h-4 w-4" />
            {buttonText}
          </Button>
        );
    }
  };
  
  const contentWidth = size === 'sm' ? 'w-72' : size === 'lg' ? 'w-[500px]' : 'w-96';
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        {triggerContent()}
      </PopoverTrigger>
      <PopoverContent className={cn(contentWidth, "p-0")}>
        <div className="flex flex-col">
          <div className="bg-muted-foreground/10 p-3">
            <h4 className="font-medium">{helpContent.title || topic}</h4>
            {helpContent.subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{helpContent.subtitle}</p>
            )}
          </div>
          <div className="p-4 text-sm">
            {children || (
              <div className="space-y-3">
                {helpContent.sections?.map((section, idx) => (
                  <div key={idx}>
                    {section.heading && (
                      <h5 className="font-medium mb-1 text-sm">{section.heading}</h5>
                    )}
                    <p className="text-sm">{section.content}</p>
                    
                    {section.bulletPoints && section.bulletPoints.length > 0 && (
                      <ul className="mt-2 list-disc pl-4 text-sm">
                        {section.bulletPoints.map((point, i) => (
                          <li key={i} className="mb-1">{point}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {helpContent.docLink && (
            <div className="bg-muted-foreground/5 border-t p-3">
              <Button 
                variant="link" 
                className="p-0 h-auto text-sm flex gap-1 items-center"
                onClick={() => window.open(helpContent.docLink, '_blank')}
              >
                <BookOpen className="h-4 w-4" />
                View complete documentation
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Help content database
function getHelpContent(topic: string) {
  const helpDatabase: Record<string, any> = {
    'retention-policy': {
      title: 'Retention Policies',
      subtitle: 'How certificate data retention works',
      description: 'Retention policies determine how long certificate records are kept before they are archived or deleted. Different states have different requirements.',
      docLink: '/admin/documentation/retention',
      sections: [
        {
          heading: 'Policy Components',
          content: 'Each retention policy includes a target data type, retention period, and criteria for when data should be archived or deleted.',
          bulletPoints: [
            'Table Name: Database table this policy applies to',
            'State: Applies only to certificates from this state (if specified)',
            'Retention Period: Number of years to retain data before archiving',
            'Active Status: Whether the policy is currently enforced'
          ]
        },
        {
          heading: 'State Requirements',
          content: 'Different states have different minimum retention periods:',
          bulletPoints: [
            'Florida: 5 years minimum',
            'California: 3 years minimum',
            'Texas: 3 years minimum',
            'Default: 5 years if not specified'
          ]
        }
      ]
    },
    'retention-execution': {
      title: 'Retention Policy Execution',
      description: 'Manually process retention policies or run dry runs to see what records would be affected before making changes.',
      docLink: '/admin/documentation/retention-execution',
      sections: [
        {
          heading: 'Execution Modes',
          content: 'Retention can be executed in two ways:',
          bulletPoints: [
            'Dry Run: Shows affected records without taking action',
            'Live Mode: Actually processes records according to retention policy'
          ]
        },
        {
          heading: 'Scheduling Options',
          content: 'You can choose to run retention policies manually or set up automated scheduled execution.'
        }
      ]
    },
    'certificate-export': {
      title: 'Certificate Export Options',
      description: 'Export certificate data in various formats for reporting, backup, or regulatory compliance.',
      docLink: '/admin/documentation/certificate-exporting',
      sections: [
        {
          heading: 'Export Formats',
          content: 'You can export certificate data in several formats:',
          bulletPoints: [
            'CSV: Basic spreadsheet format compatible with most systems',
            'Excel: Enhanced format with better formatting',
            'PDF: Formal reports with proper formatting and pagination',
            'Regulatory: State-specific formats required for submission'
          ]
        },
        {
          heading: 'Inspection Mode',
          content: 'Generate for Inspection mode creates exports specifically formatted for regulatory review, including all required fields and proper formatting for each state.'
        }
      ]
    },
    'compliance-notifications': {
      title: 'Compliance Notifications',
      description: 'Receive alerts about upcoming deadlines, policy changes, and compliance requirements.',
      sections: [
        {
          heading: 'Notification Types',
          content: 'The system provides several types of notifications:',
          bulletPoints: [
            'Deadlines: Upcoming report due dates',
            'Retention Events: When retention processing is needed',
            'Regulatory Updates: Changes to state requirements'
          ]
        }
      ]
    }
  };
  
  return helpDatabase[topic] || {
    title: topic,
    description: 'Information about this feature will be added soon.',
  };
}
